import React, { useMemo, useCallback, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { Box, IconButton } from "@mui/material";
import { Widgets } from "./Widgets";
import DraggableWidget from "./DraggableWidget";

interface WorkingAreaProps {
  formId: string;
  formWidgets: { id: string; widgetType: string; config: any }[];
  mandatoryLabels: string[];
  onSelectWidget: (id: string) => void;
  selectedWidgetId: string | null;
  updateConfig: (id: string, newConfig: any) => void;
  removeWidget: (id: string) => void;
  onDropWidget: (formId: string, widget: any) => void;
  onReorderWidgets?: (formId: string, fromIndex: number, toIndex: number) => void;
}

const WorkingArea: React.FC<WorkingAreaProps> = ({
  formId,
  formWidgets,
  mandatoryLabels,
  onSelectWidget,
  selectedWidgetId,
  updateConfig,
  removeWidget,
  onDropWidget,
  onReorderWidgets,
}) => {
  
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dropZoneRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  
  
  // Auto-scroll functionality
  const handleAutoScroll = useCallback((clientY: number) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const scrollThreshold = 50; 
    const scrollSpeed = 10; 

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    
    // Check if we need to scroll up
    if (clientY - containerRect.top < scrollThreshold) {
      scrollIntervalRef.current = setInterval(() => {
        if (container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
        } else {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
      }, 16); 
    }
    // Check if we need to scroll down
    else if (containerRect.bottom - clientY < scrollThreshold) {
      scrollIntervalRef.current = setInterval(() => {
        if (container.scrollTop < container.scrollHeight - container.clientHeight) {
          container.scrollTop += scrollSpeed;
        } else {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
      }, 16); 
    }
  }, []);
  
  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);
  
  const [{ isOver }, dropRef] = useDrop({
    accept: "WIDGET",
    canDrop: (item: any) => true,
    
    drop: (item: any, monitor) => {
      console.log("Dropped item:", item);
      
      // Stop auto-scrolling when drop occurs
      stopAutoScroll();
      
      if (item.fromFormId === formId && onReorderWidgets && dragOverIndex !== null) {
        const fromIndex = item.index;
        const toIndex = dragOverIndex;
        
        if (fromIndex !== toIndex) {
          onReorderWidgets(formId, fromIndex, toIndex);
        }
      }
      else if (item.fromFormId === "widget-panel" || item.fromFormId !== formId) {
        onDropWidget(formId, item.widget);
      }
      
      setDragOverIndex(null);
    },
    
    hover: (item, monitor) => {
      if (!monitor.canDrop()) return;
      
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      
      // Trigger auto-scroll based on mouse position
      handleAutoScroll(clientOffset.y);
      
      if (item.fromFormId === formId && onReorderWidgets) {
        let hoverIndex = formWidgets.length; 
        
        for (let i = 0; i < dropZoneRefs.current.length; i++) {
          const dropZone = dropZoneRefs.current[i];
          if (!dropZone) continue;
          
          const hoverBoundingRect = dropZone.getBoundingClientRect();
          const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;
          
          if (hoverClientY < hoverMiddleY) {
            hoverIndex = i;
            break;
          }
        }
        if (item.index < hoverIndex) {
          hoverIndex = Math.max(0, hoverIndex - 1);
        }
        
        setDragOverIndex(hoverIndex);
      }
    },
    
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Clean up auto-scroll interval on unmount
  React.useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const handleDragEnd = () => {
      setDragOverIndex(null);
      stopAutoScroll();
    };
  
    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [stopAutoScroll]);

  React.useEffect(() => {
    if (!isOver) {
      setDragOverIndex(null);
    }
  }, [isOver]);
  
  

  const handleWidgetClick = useCallback(
    (id: string) => onSelectWidget(id),
    [onSelectWidget]
  );

  const handleRemoveWidget = useCallback(
    (id: string) => removeWidget(id),
    [removeWidget]
  )
  const renderedWidgets = useMemo(() => {
    const widgets = formWidgets.map((widget, index) => {
      const widgetTemplate = Widgets.find((w) => w.id === widget.widgetType);
      if (!widgetTemplate) {
        console.warn("Missing widgetTemplate for widgetType:", widget.widgetType);
        return null;
      }
      const label = widget.config?.label?.toLowerCase().trim() || "";
      const isMandatory = mandatoryLabels.includes(label);
      return (
        <React.Fragment key={widget.id}>
          {/* Drop zone before each widget */}
          <div
  ref={(el) => (dropZoneRefs.current[index] = el)}
  style={{
    height: dragOverIndex === index ? '30px' : '8px',
    backgroundColor: dragOverIndex === index ? '#e3f2fd' : 'transparent',
    border: dragOverIndex === index ? '2px dashed #2196f3' : '1px dashed transparent',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    margin: dragOverIndex === index ? '6px 0' : '2px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2196f3',
    fontSize: dragOverIndex === index ? '0.8rem' : '0',
    fontWeight: 500,
    fontStyle: 'italic',
  }}
>
  {dragOverIndex === index ? 'Drop here' : null}
</div>

          
          <DraggableWidget
            widget={widget}
            formId={formId}
            widgetTemplate={widgetTemplate}
            isSelected={selectedWidgetId === widget.id}
            onClick={handleWidgetClick}
            onRemove={handleRemoveWidget}
            updateConfig={updateConfig}
            index={index}
            isMandatory={isMandatory}
          />
        </React.Fragment>
      );
    });
    
    // Add final drop zone at the end
    widgets.push(
      <div
      key="final-drop-zone"
      ref={(el) => (dropZoneRefs.current[formWidgets.length] = el)}
      style={{
        height: dragOverIndex === formWidgets.length ? '30px' : '8px',
        backgroundColor: dragOverIndex === formWidgets.length ? '#e3f2fd' : 'transparent',
        border: dragOverIndex === formWidgets.length ? '2px dashed #2196f3' : '1px dashed transparent',
        borderRadius: '6px',
        transition: 'all 0.2s ease',
        margin: dragOverIndex === formWidgets.length ? '6px 0' : '2px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#2196f3',
        fontSize: dragOverIndex === formWidgets.length ? '0.8rem' : '0',
        fontWeight: 500,
        fontStyle: 'italic',
      }}
    >
      {dragOverIndex === formWidgets.length ? 'Drop here' : null}
    </div>    
    );
    
    return widgets;
  }, [formWidgets, selectedWidgetId, handleWidgetClick, handleRemoveWidget, formId, dragOverIndex, mandatoryLabels]);
  
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    dropRef(node);
    containerRef.current = node;
  }, [dropRef]);

  return (
    <Box
      ref={combinedRef}
      sx={{
        height: "660px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "4px", 
        padding: 2,
        overflowY: "auto",
        margin: "auto",
        border: isOver ? "2px dashed #2196f3" : "2px solid #ddd",
        transition: "border 0.2s ease", 
      }}
    >
      {renderedWidgets}
    </Box>
  );
};

export default WorkingArea;